import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Brain, Database, Link2, Shuffle,
  Sparkles, Users, ShieldCheck, BookOpen, Stethoscope, FlaskConical,
  AlertCircle, Lock, Microscope, FileText,
  ChevronRight, ChevronDown, Activity, Bell, Search, ArrowLeft,
} from 'lucide-react'
import clsx from 'clsx'

// ─── Types ────────────────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'open-source', label: '1. Open Sources Used' },
  { id: 'differentiators', label: '2. Key Differentiators' },
  { id: 'gaps', label: '3. What Was Missing' },
  { id: 'manual', label: '4. User Manual' },
  { id: 'benefits', label: '5. User Benefits' },
]

// ─── Section 1 ────────────────────────────────────────────────────────────────

const OPEN_SOURCES = [
  {
    icon: Brain,
    name: 'OpenCog Hyperon',
    version: 'hyperon==0.2.10',
    badge: 'MeTTa Runtime',
    iconBg: 'bg-violet-50',
    iconColor: 'text-violet-600',
    badgeCls: 'bg-violet-100 text-violet-700',
    what: 'Hyperon is the next-generation OpenCog AGI framework providing the MeTTa self-modifying AI language and its Python runtime. MeTTa represents knowledge and inference rules as s-expressions that can rewrite themselves.',
    how: 'All causal inference rules are written as MeTTa source strings and executed through the hyperon.MeTTa() runner. The PLN engine submits an atom list as MeTTa facts, runs the rule set, and parses returned ImplicationLink s-expressions into Python CausalChain objects. The monitor rule engine similarly encodes all seven alert types as MeTTa defrule declarations.',
    code: `(defrule high-hba1c-trend
  (ImplicationLink (BiomarkerAtom hba1c)
    (status prediabetic)
    (stv 0.7 0.8)))`,
    note: 'Every invocation is wrapped in try/except — if MeTTa fails or the library is absent, execution falls back to a native Python equivalent producing the same data contract.',
  },
  {
    icon: Database,
    name: 'Hyperon DAS',
    version: 'hyperon-das==0.9.1',
    badge: 'Distributed Atomspace',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
    badgeCls: 'bg-blue-100 text-blue-700',
    what: 'The Distributed Atomspace (DAS) is OpenCog Hyperon\'s scalable knowledge-graph store. It supports both in-process in-memory mode (development) and a remote server mode (production) via a uniform query API.',
    how: 'Every health Atom written by the ingestion service is inserted into the DAS partition owned by that user\'s DID. At startup, four foundational MKG nodes are seeded — ReferenceRangeAtom, MedicalRuleAtom, PenetranceAtom, CausalTemplateAtom — from WHO, ACC/AHA, ClinVar, and Rejuve.Bio sources. The reasoning engine queries these to ground personal causal chains against peer-reviewed evidence.',
    code: `class _StubDAS:
    def add_node(self, spec): ...
    def query(self, pattern): return []`,
    note: 'A _StubDAS class provides a fully in-process fallback when hyperon-das is not installed, ensuring the entire backend runs without any SingularityNET infrastructure during local development.',
  },
  {
    icon: Link2,
    name: 'cosmpy / ASI:Chain',
    version: 'cosmpy==0.9.2',
    badge: 'Phase 2',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    badgeCls: 'bg-amber-100 text-amber-700',
    what: 'cosmpy is the Python SDK for the Fetch.ai Cosmos-based ASI:Chain blockchain. It provides ledger client connections, Cosmos SDK message broadcasting, and ASI/FET/OCEAN token operations.',
    how: 'Three subsystems depend on cosmpy: consent contracts broadcast as on-chain messages (immutable audit log), a token economy priced in ASI tokens (PLN run: 0.40 ASI, MOSES run: 1.00 ASI), and DID registration anchored on ASI:Chain. Phase 1 stubs these; Phase 2 activates them with a funded wallet.',
    code: `def _try_chain_record(contract):
    try:
        from cosmpy.aerial.client import LedgerClient
        ...
    except ImportError:
        return  # silently degrade`,
    note: 'Phase 1 returns stub balances and stores ConsentContracts in SQLite with an on-chain broadcast prepared but commented out pending a funded wallet.',
  },
  {
    icon: Shuffle,
    name: 'MOSES / asmoses',
    version: 'asmoses (optional)',
    badge: 'Pattern Mining',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    badgeCls: 'bg-emerald-100 text-emerald-700',
    what: 'MOSES (Meta-Optimizing Semantic Evolutionary Search) is the OpenCog program-synthesis algorithm for learning compact if-then rules from data. asmoses is its Python binding for multigenerational pattern discovery.',
    how: 'The MOSES engine uses asmoses to discover multigenerational health patterns across a family\'s combined Atom history. Because asmoses does not yet have a stable PyPI release, the engine falls back to _native_moses() — a combinatorial Python implementation that finds conditions shared across ≥2 generations and classifies them as genetic or lifestyle.',
    code: `for cond, members in condition_to_members.items():
    gens = {generation_of(m) for m in members}
    if len(gens) >= 2:
        yield MOSESPatternAtom(
            condition=cond,
            pattern_type=classify(cond, gens))`,
    note: 'The native fallback produces the same MOSESPatternAtom data structure that real MOSES would produce — the UI and downstream reasoning are identical.',
  },
]

// ─── Section 2 ────────────────────────────────────────────────────────────────

const DIFFERENTIATORS = [
  {
    icon: Sparkles,
    title: 'Personal Causal Models, Not Population Averages',
    body: 'Most health apps surface recommendations from population studies. FamilyPortal builds a causal model from your own longitudinal data. PLN runs across your personal Atomspace, finding recurring patterns where one atom type reliably precedes another within a measured lag window. The resulting CausalChain carries a strength computed from your count of observations — not borrowed from a population average.',
  },
  {
    icon: Users,
    title: 'Four-Generation Family Health Graph',
    body: 'The data model supports four biological generations plus lateral relationships (siblings, half-siblings, spouses, adopted members). Deceased grandparents appear as ProxyMemberAtom nodes with a 0.7× confidence penalty, making self-reported uncertainty explicit. MOSES mines patterns across all family members where consent exists, identifying conditions recurring in ≥2 generations and separating genetic risk from modifiable fraction.',
  },
  {
    icon: ShieldCheck,
    title: 'Immutable, Granular, User-Owned Consent',
    body: 'Data sharing is governed by ConsentContract objects, not privacy settings. Each contract specifies which atom types are shared, the permission level (read / read_write / monitor_only), and an optional expiry. Contracts are broadcast on ASI:Chain, creating an immutable audit log. The monitor_only level lets a caregiver receive real-time alerts without ever seeing raw atom values.',
  },
  {
    icon: BookOpen,
    title: 'Transparent, Citable Reasoning',
    body: 'Every causal chain and every alert carries mkg_citations — PubMed IDs and clinical guideline references sourced from the Medical Knowledge Graph. Users can trace any inference back to peer-reviewed evidence. Chains without MKG grounding are explicitly flagged and excluded from the Doctor Brief until grounded.',
  },
  {
    icon: Brain,
    title: 'Closed Feedback Loop',
    body: 'Users can challenge any causal chain via /reason/feedback. This writes a NegativeObsAtom and reduces the chain\'s strength by 0.15 (NARS evidence revision). If strength falls below 0.3, the chain is demoted from confirmed to candidate. The inference engine learns from user corrections without requiring a model retrain.',
  },
  {
    icon: FlaskConical,
    title: 'Research Participation with Structural Anonymization',
    body: 'Via the Rejuve.Bio Bridge, users can contribute their Atomspace to longevity research. The anonymization pipeline preserves causal structure while removing identifying content: age bucketed to 5-year bands, Laplace noise (ε ≤ 1.0), timestamps jittered ±3 days, k-anonymity k ≥ 5. Users earn RJV tokens for validated exports and receive RESEARCH_MATCH alerts when Rejuve findings match their own patterns.',
  },
]

// ─── Section 3 ────────────────────────────────────────────────────────────────

const GAPS = [
  {
    num: '3.1',
    title: 'Health-Domain Ontology for Atomspace',
    body: 'Hyperon DAS is a general-purpose knowledge graph with no schema for HeartRateAtom, BiomarkerAtom, or any health concept. FamilyPortal defines a 15-type health atom ontology with typed payloads, provenance fields, and a strength/count epistemics model aligned with PLN\'s truth-value semantics. Every atom type includes source validation, recorded_at/created_at distinction, and a superseded flag for versioning.',
  },
  {
    num: '3.2',
    title: 'Temporal PLN for Longitudinal Data',
    body: 'Standard Hyperon PLN operates on static knowledge graphs, not timestamped longitudinal streams. Discovering that "ActivityAtom drops predict BiomarkerAtom decline 21–45 days later" requires temporal PLN: sort atoms by timestamp, define lag windows as first-class ImplicationLink parameters, compute hit rates within those windows, and apply NARS evidence revision to combine prior chains with new observations. FamilyPortal\'s _native_pln() implements this from scratch.',
  },
  {
    num: '3.3',
    title: 'Multi-Party Consent Architecture for Cross-Atomspace Inference',
    body: 'OpenCog Hyperon has no built-in concept of data ownership or access control between Atomspaces. Running MOSES across a family\'s combined Atomspace requires a consent model that is granular to atom type level, permission-leveled, cryptographically anchored (on-chain ConsentContract), and scoped so inference results can be returned without exposing raw atoms from consented members.',
  },
  {
    num: '3.4',
    title: 'Proxy Members for Non-User Family Nodes',
    body: 'No SingularityNET tool addresses the common situation: the most important data contributors (grandparents, deceased relatives) cannot be users of any software. FamilyPortal introduces ProxyMemberAtom — carrying known conditions as synthetic ClinicalObsAtom atoms with 0.7× confidence weighting, participating in MOSES pattern mining with explicit uncertainty propagation.',
  },
  {
    num: '3.5',
    title: 'MKG Evidence Grounding Pipeline',
    body: 'Hyperon DAS provides storage and query — but no mechanism for populating a Medical Knowledge Graph from peer-reviewed sources or grounding personal inference against it. FamilyPortal builds a full MKG service with built-in reference ranges (WHO, ACC/AHA), drug-nutrient interactions, hereditary penetrance data (ClinVar), clinical guideline rules (ICD-11, SNOMED CT), a curation gate, version history, and a Rejuve.Bio sync pipeline.',
  },
  {
    num: '3.6',
    title: 'Doctor-Ready Clinical Summary Export',
    body: 'SingularityNET tools produce inference graphs — no existing tool converts Atomspace inference into a patient-facing clinical document. FamilyPortal\'s Doctor Brief selects top 3–5 causal chains ranked by confidence and urgency, formats each with MKG citations and recommended actions, and exports as HL7 FHIR R4 Bundle (Composition + Observation resources) or a shareable PDF with 7-day expiry.',
  },
  {
    num: '3.7',
    title: 'Longitudinal Caregiver Interface',
    body: 'The SingularityNET agent ecosystem has no concept of a care relationship with differential data access. FamilyPortal\'s caregiver model provides a dashboard aggregating alerts across all consenting family members, tracks medication adherence, detects gait deterioration via GaitAtom trend analysis, and supports ContextAtom logging for caregivers who hold read_write consent — without exposing raw health data.',
  },
]

// ─── Section 4 ────────────────────────────────────────────────────────────────

const MANUAL_STEPS = [
  {
    icon: Activity,
    num: '4.1',
    title: 'Getting Started',
    steps: [
      { label: 'Create an account', detail: 'Navigate to FamilyPortal and click Create account. Enter your email, password, and name. The system assigns you a DID that will own your health data.' },
      { label: 'Add your first data', detail: 'Go to Add Data in the left navigation. Select an atom type and fill in the template: Sleep (hours, quality, REM %), Heart Rate (avg BPM), Biomarker (lab name, value, unit), Symptom (name, severity 1–10), Activity (steps, active minutes), or Medication (name, dose, frequency).' },
      { label: 'Run reasoning', detail: 'Return to the Dashboard and click Run reasoning. PLN runs across your Atomspace and the Monitor service fires. Active Alerts and Causal Chains appear after a few seconds.' },
    ],
  },
  {
    icon: Bell,
    num: '4.2',
    title: 'Understanding Your Alerts',
    steps: [
      { label: 'Alert statuses', detail: 'Go to Alerts. Active = requires attention now. Snoozed = deferred. Acted on = recommendation followed. Dismissed = reviewed and rejected.' },
      { label: 'Alert anatomy', detail: 'Each alert shows its type (PRECURSOR, TREND, INTERACTION, CAREGIVER, MISSED_CHECK), urgency (1–5), a plain-language message, and a recommended action.' },
      { label: 'Responding', detail: 'Click Dismiss if you disagree, Acted on if you followed the recommendation, or Check now to re-run the monitor after adding new data.' },
    ],
  },
  {
    icon: Brain,
    num: '4.3',
    title: 'Reading Causal Chains',
    steps: [
      { label: 'Chain anatomy', detail: 'Each chain reads: [Antecedent] → [Consequent] · Lag: 21–45 days · Confidence: 72% · Status: Confirmed · Source: PMID:12345678.' },
      { label: 'Status meanings', detail: 'Confirmed = ≥3 observations, confidence ≥0.5, grounded in MKG. Candidate = ≥2 observations or confidence 0.3–0.5. Hypothesis = early pattern, fewer than 3 observations or ungrounded.' },
      { label: 'Challenging a chain', detail: 'Click Feedback on the Reasoning page. This reduces the chain\'s confidence and, with accumulating feedback, demotes its status from confirmed to candidate.' },
    ],
  },
  {
    icon: Activity,
    num: '4.4',
    title: 'Using the Timeline',
    steps: [
      { label: 'Browse your data', detail: 'Go to Timeline to see all health data in reverse chronological order. Each entry shows values, data source, and recording date.' },
      { label: 'Filter by type', detail: 'Use the Filter by type dropdown to isolate a single atom type (e.g. only Biomarker readings). Useful for spotting manual entry errors before running reasoning.' },
    ],
  },
  {
    icon: Users,
    num: '4.5',
    title: 'Building Your Family Graph',
    steps: [
      { label: 'Add a member', detail: 'Go to Family Graph → Add member. Fill in relation (biological parent, grandparent, sibling, spouse, etc.), display name, birth year (optional), and known conditions.' },
      { label: 'Proxy members', detail: 'Non-user family members — including deceased relatives — are created as proxy members carrying 70% weight to reflect self-report uncertainty.' },
      { label: 'Run MOSES', detail: 'With ≥1 family member added, click Run MOSES. The Hereditary Risk Profile populates with conditions recurring across generations, showing risk range, estimated onset age, and modifiable fraction.' },
    ],
  },
  {
    icon: Stethoscope,
    num: '4.6',
    title: 'Doctor Brief',
    steps: [
      { label: 'Generate the brief', detail: 'Go to Doctor Brief. With at least one reasoning run completed, the brief auto-populates: top 3–5 findings with data, evidence counts, and clinical citations; active high-urgency alerts (urgency ≥3).' },
      { label: 'Export & share', detail: 'Click Share to generate a link valid 7 days. Export as FHIR R4, PDF, or plain text. Click Schedule visit to book an appointment — the brief auto-updates 24 hours before.' },
      { label: 'Close the loop', detail: 'After the visit, log outcomes (diagnosis, medications, tests, follow-up date). These store as ClinicalObsAtom entries and feed into the next reasoning cycle.' },
    ],
  },
  {
    icon: ShieldCheck,
    num: '4.7',
    title: 'Managing Data Sharing (Consent)',
    steps: [
      { label: 'Grant access', detail: 'Go to Consent Manager → Grant access. Enter the grantee DID, toggle which atom types to share, and choose Read, Read & write, or Monitor only. Set a purpose and optional expiry.' },
      { label: 'Revoke access', detail: 'Click the trash icon next to any consent entry to revoke immediately. All future data access under that contract stops.' },
    ],
  },
  {
    icon: FlaskConical,
    num: '4.8',
    title: 'Participating in Research (Optional)',
    steps: [
      { label: 'Opt in', detail: 'Click Opt in to Rejuve.Bio research on the Consent Manager page. Your data is anonymized with differential privacy (ε ≤ 1.0) and k-anonymity (k ≥ 5) before any export.' },
      { label: 'Opt out', detail: 'Opt out at any time. No further exports will occur. Prior exports are anonymized and cannot be recalled.' },
      { label: 'Rewards', detail: 'Earn RJV tokens for validated contributions and receive RESEARCH_MATCH alerts when Rejuve findings match patterns in your own data.' },
    ],
  },
]

// ─── Section 5 ────────────────────────────────────────────────────────────────

const BENEFITS = [
  {
    icon: Search,
    title: 'Answers From Your Own Data',
    body: 'FamilyPortal does not offer generic health tips. It builds a causal model from your own longitudinal records — your specific iron trajectory, your actual sleep-to-recovery relationship. The difference between "studies suggest low iron affects energy" and "your data shows your ferritin below 35 ng/mL reliably precedes fatigue onset 14–21 days later, supported by 6 observations."',
  },
  {
    icon: AlertCircle,
    title: 'Early Detection of Developing Patterns',
    body: 'The monitor service runs continuously, looking for patterns that match alert rules before they become acute problems. A PRECURSOR alert fires when early-stage markers appear — not after a threshold is crossed but when the trajectory suggests it will be, giving users time to act before symptoms require clinical intervention.',
  },
  {
    icon: Users,
    title: 'Family Health Context Without Privacy Exposure',
    body: 'Users can incorporate family health history into their personal risk profile without relatives ever sharing raw data. Proxy members encode known conditions as structured nodes. Where family members have granted consent, MOSES mines cross-Atomspace patterns — but only under explicit, revocable, atom-type-level consent.',
  },
  {
    icon: Stethoscope,
    title: 'Doctor Appointments That Use All the Data',
    body: 'FamilyPortal\'s Doctor Brief compiles the most clinically relevant findings, ranks them by confidence and urgency, attaches supporting evidence counts and PubMed citations, and formats the result as a HL7 FHIR R4 document that EHR systems can ingest. Doctors receive structured, citable input. Patients arrive prepared.',
  },
  {
    icon: Lock,
    title: 'Ownership and Control of Health Data',
    body: 'A user\'s health data belongs exclusively to them, anchored to a DID on ASI:Chain. Sharing is governed by explicit, auditable ConsentContracts — not platform privacy settings. Every grant, update, and revocation is recorded on-chain. Data can be exported or deleted at any time. No advertisement business model.',
  },
  {
    icon: Microscope,
    title: 'Participation in Longevity Science',
    body: 'Through the optional Rejuve.Bio bridge, users contribute to longevity research with mathematical privacy guarantees: k-anonymity (k ≥ 5) and differential privacy (ε ≤ 1.0). The contribution preserves causal structure while making individual re-identification computationally infeasible. Users earn RJV tokens and receive feedback when their data contributes to a validated scientific finding.',
  },
  {
    icon: FileText,
    title: 'Reasoning That Can Be Questioned',
    body: 'Every causal chain and every alert is accompanied by the evidence that produced it and the MKG citations that ground it. Users who disagree can submit feedback that immediately adjusts the chain\'s confidence. This is a transparent inference engine that presents outputs as claims, shows its working, and accepts correction — not a black-box system.',
  },
]

// ─── Sub-components ───────────────────────────────────────────────────────────

function Badge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', className)}>
      {children}
    </span>
  )
}

function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="mt-3 bg-gray-950 text-green-400 text-xs font-mono rounded-lg p-3 overflow-x-auto whitespace-pre-wrap leading-relaxed">
      {code}
    </pre>
  )
}

function OpenSourceCard({ lib }: { lib: typeof OPEN_SOURCES[0] }) {
  const [open, setOpen] = useState(false)
  const Icon = lib.icon
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className={clsx('p-2 rounded-lg shrink-0', lib.iconBg)}>
          <Icon size={18} className={lib.iconColor} />
        </div>
        <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900">{lib.name}</span>
          <Badge className={lib.badgeCls}>{lib.badge}</Badge>
          <code className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{lib.version}</code>
        </div>
        {open
          ? <ChevronDown size={15} className="text-gray-400 shrink-0" />
          : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">What it is</p>
            <p className="text-sm text-gray-700 leading-relaxed">{lib.what}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">How FamilyPortal uses it</p>
            <p className="text-sm text-gray-700 leading-relaxed">{lib.how}</p>
            <CodeBlock code={lib.code} />
          </div>
          <p className="text-xs text-gray-500 italic border-l-2 border-gray-200 pl-3 leading-relaxed">{lib.note}</p>
        </div>
      )}
    </div>
  )
}

function ManualCard({ step }: { step: typeof MANUAL_STEPS[0] }) {
  const [open, setOpen] = useState(false)
  const Icon = step.icon
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="p-2 rounded-lg bg-brand-50 shrink-0">
          <Icon size={17} className="text-brand-600" />
        </div>
        <div className="flex-1">
          <span className="text-xs text-brand-500 font-mono font-semibold">{step.num}</span>
          <p className="font-semibold text-gray-900 text-sm">{step.title}</p>
        </div>
        {open
          ? <ChevronDown size={15} className="text-gray-400 shrink-0" />
          : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-3">
          {step.steps.map((s, i) => (
            <div key={i} className="flex gap-3">
              <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-600 text-white text-xs flex items-center justify-center shrink-0 font-bold">
                {i + 1}
              </span>
              <div>
                <p className="text-sm font-semibold text-gray-900">{s.label}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{s.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DevReport() {
  const [activeSection, setActiveSection] = useState('open-source')
  const navigate = useNavigate()
  const location = useLocation()
  const isPublic = location.pathname === '/report'

  const scrollTo = (id: string) => {
    setActiveSection(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className={clsx('flex flex-col', isPublic ? 'min-h-screen bg-gray-50' : 'h-full')}>

      {/* Public top bar */}
      {isPublic && (
        <header className="bg-brand-900 border-b border-brand-700/50 px-6 h-14 flex items-center justify-between shrink-0">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-brand-100 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={15} />
            Back to FamilyPortal
          </button>
          <button
            onClick={() => navigate('/login')}
            className="text-sm bg-brand-600 hover:bg-brand-500 text-white px-4 py-1.5 rounded-lg font-medium transition-colors"
          >
            Get started
          </button>
        </header>
      )}

    <div className="flex flex-1 overflow-hidden">

      {/* Sidebar TOC */}
      <aside className="hidden lg:flex flex-col w-52 shrink-0 border-r border-gray-200 bg-white py-6 px-4 sticky top-0 h-screen overflow-y-auto">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Contents</p>
        <nav className="space-y-0.5">
          {SECTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={clsx(
                'w-full text-left px-2 py-1.5 rounded-md text-sm transition-colors',
                activeSection === s.id
                  ? 'bg-brand-50 text-brand-700 font-medium'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
              )}
            >
              {s.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-gray-100">
          <p className="text-xs text-gray-400 px-1 leading-relaxed">v0.2 · Proof-of-Concept · May 2026</p>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Badge className="bg-brand-100 text-brand-700">Development Report</Badge>
              <Badge className="bg-gray-100 text-gray-600">v0.2 · May 2026</Badge>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">FamilyPortal</h1>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xl">
              Architecture rationale, key differentiators, gaps filled in the SingularityNET ecosystem,
              user manual, and user benefits — all in one place.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {['OpenCog Hyperon', 'Distributed Atomspace', 'ASI:Chain', 'FastAPI', 'React'].map(t => (
                <Badge key={t} className="bg-gray-100 text-gray-600">{t}</Badge>
              ))}
            </div>
          </div>

          {/* ── 1. Open Sources ── */}
          <section id="open-source" className="mb-14 scroll-mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="font-mono text-brand-500">1</span>
              SingularityNET Open Sources Used
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Four open-source libraries, each serving a distinct architectural layer. Click any card to expand.
            </p>
            <div className="space-y-3">
              {OPEN_SOURCES.map(lib => <OpenSourceCard key={lib.name} lib={lib} />)}
            </div>
          </section>

          {/* ── 2. Differentiators ── */}
          <section id="differentiators" className="mb-14 scroll-mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="font-mono text-brand-500">2</span>
              Key Differentiators
            </h2>
            <div className="grid gap-4">
              {DIFFERENTIATORS.map(d => {
                const Icon = d.icon
                return (
                  <div key={d.title} className="bg-white border border-gray-200 rounded-xl p-5 flex gap-4">
                    <div className="p-2 rounded-lg bg-brand-50 shrink-0 h-fit">
                      <Icon size={17} className="text-brand-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1 text-sm">{d.title}</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{d.body}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>

          {/* ── 3. Gaps ── */}
          <section id="gaps" className="mb-14 scroll-mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="font-mono text-brand-500">3</span>
              What Was Missing in Existing SingularityNET Projects
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Applying general-purpose AGI building blocks to personal health required solving
              problems that do not exist in the upstream libraries.
            </p>
            <div className="space-y-3">
              {GAPS.map(g => (
                <div key={g.num} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-mono text-brand-500 font-semibold shrink-0">{g.num}</span>
                    <p className="font-semibold text-gray-900 text-sm">{g.title}</p>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{g.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── 4. Manual ── */}
          <section id="manual" className="mb-14 scroll-mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
              <span className="font-mono text-brand-500">4</span>
              User Manual
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              Eight workflows covering the full service lifecycle. Click any card to expand step-by-step instructions.
            </p>
            <div className="space-y-3">
              {MANUAL_STEPS.map(s => <ManualCard key={s.num} step={s} />)}
            </div>
          </section>

          {/* ── 5. Benefits ── */}
          <section id="benefits" className="mb-14 scroll-mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
              <span className="font-mono text-brand-500">5</span>
              User Benefits
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {BENEFITS.map(b => {
                const Icon = b.icon
                return (
                  <div key={b.title} className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 rounded-md bg-brand-50 shrink-0">
                        <Icon size={15} className="text-brand-600" />
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{b.title}</p>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.body}</p>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 text-xs text-gray-400 text-center leading-relaxed">
            FamilyPortal Proof-of-Concept · OpenCog Hyperon + Distributed Atomspace + ASI:Chain + FastAPI + React
          </div>

        </div>
      </div>
    </div>
    </div>
  )
}
